import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, of, take } from 'rxjs';
import { App } from 'app/core/app/app.model';
import { AppService } from 'app/core/app/app.service';
import { WhatsNewAnnouncement } from './whats-new.types';
import { resolveCta } from './whats-new.cta';
import {
  WhatsNewDialogComponent,
  WhatsNewDialogData,
} from './whats-new-dialog.component';
import {
  accountKey,
  ANONYMOUS_ACCOUNT,
  forgetAccount,
  pendingAnnouncement,
  readSeenState,
  resolveQueryTrigger,
  writeSeen,
} from './whats-new.storage';

/**
 * Decides which feature announcement to show, and remembers what was shown.
 *
 * The state lives in `localStorage` rather than on a server: none of the
 * backends the console talks to has a per-user store, and Cortex — the only
 * candidate — is switched off entirely in the staging build. The cost is that a
 * new device shows an announcement once more, which is normal for this kind of
 * message.
 *
 * The rules themselves sit in `whats-new.storage.ts`, free of Angular so they
 * can be tested on their own.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private _account = ANONYMOUS_ACCOUNT;
  private _queueRunning = false;

  /**
   * Announcements already shown in this session, whether or not they were
   * dismissed for good.
   *
   * Without this the queue would loop: somebody who unticks "Don't show this
   * again" is deliberately *not* written to storage, so the very next look at
   * the pending list would return the same announcement and reopen it.
   */
  private readonly _shownThisSession = new Set<string>();

  constructor(
    private _matDialog: MatDialog,
    private _appService: AppService,
  ) {}

  /** Point the service at the signed-in account. */
  setAccount(email: string | null | undefined): void {
    this._account = accountKey(email);
  }

  /**
   * Start a fresh run, forgetting what was already shown on screen.
   *
   * Signing out and back in is a navigation, not a page load — this service is
   * a singleton and survives it. Without this, an announcement that somebody
   * kept by unticking "Don't show this again" would stay suppressed for the
   * rest of the browser tab, including the next sign-in.
   */
  beginSession(): void {
    this._shownThisSession.clear();
    this._queueRunning = false;
  }

  /** The newest announcement still due for this account, or `null`. */
  pending(): WhatsNewAnnouncement | null {
    return pendingAnnouncement(
      readSeenState(localStorage),
      this._account,
      undefined,
      undefined,
      this._shownThisSession,
    );
  }

  /**
   * Work through everything this account has not seen, one dialog at a time.
   *
   * Two announcements at once is rare but has to behave: the newest opens
   * first, the next one follows when it is closed.
   */
  showPending(): void {
    if (this._queueRunning) return;
    this._queueRunning = true;
    this._openNext();
  }

  /**
   * Handle the `?whatsnew=…` parameter, if there is one.
   *
   * Returns whether it took over — the caller then skips the normal check, so a
   * forced announcement is not immediately replaced by the pending one.
   *
   * A forced open deliberately does **not** record the announcement as seen:
   * the point is to look at it repeatedly.
   */
  handleQueryParam(value: string | null | undefined): boolean {
    const trigger = resolveQueryTrigger(value);

    if (trigger.kind === 'reset') {
      forgetAccount(localStorage, this._account);
      this._shownThisSession.clear();
      return false;
    }

    if (trigger.kind === 'open') {
      this._shownThisSession.add(trigger.announcement.id);
      this._openDialog(trigger.announcement);
      return true;
    }

    return false;
  }

  /** Remember that this account has seen an announcement. */
  markSeen(id: string): void {
    writeSeen(localStorage, this._account, id);
  }

  private _openNext(): void {
    const announcement = this.pending();
    if (!announcement) {
      this._queueRunning = false;
      return;
    }

    this._shownThisSession.add(announcement.id);
    this._openDialog(announcement, (dismissForGood) => {
      // `undefined` means the dialog was closed by Escape or the backdrop
      // rather than by a button. Treat that like the checkbox default, so the
      // outcome never depends on how somebody happened to close it.
      if (dismissForGood !== false) this.markSeen(announcement.id);
      this._openNext();
    });
  }

  /**
   * Open the dialog with its buttons already pointed somewhere.
   *
   * The project list is read first, because it decides both what the primary
   * button *does* and what it is *called* — a reader with no Fleet project is
   * offered activation, not a tutorial. `ensureApps()` is the cached-or-fetch
   * path, so in practice this resolves synchronously: the layout's resolver has
   * loaded the list long before an announcement can fire. Reading `apps$`
   * instead would be a trap, since it is seeded with `null` and would hand us
   * an empty list on a cold start.
   *
   * A failed load is not worth suppressing an announcement over — the dialog
   * opens with the neutral "go to your projects" button instead.
   */
  private _openDialog(
    announcement: WhatsNewAnnouncement,
    onClosed?: (dismissForGood?: boolean) => void,
  ): void {
    this._appService
      .ensureApps()
      .pipe(
        take(1),
        catchError(() => of([] as App[])),
      )
      .subscribe((apps) => {
        const activeApp = this._appService.activeApp;
        const data: WhatsNewDialogData = {
          announcement,
          primary: resolveCta(announcement.primaryAction, apps, activeApp),
          secondary: announcement.secondaryAction
            ? resolveCta(announcement.secondaryAction, apps, activeApp)
            : null,
        };

        const dialogRef = this._matDialog.open<
          WhatsNewDialogComponent,
          WhatsNewDialogData,
          boolean | undefined
        >(WhatsNewDialogComponent, {
          data,
          width: '92vw',
          maxWidth: '56rem',
          panelClass: 'whats-new-panel',
          backdropClass: 'whats-new-backdrop',
          autoFocus: 'dialog',
        });

        if (onClosed) dialogRef.afterClosed().subscribe(onClosed);
      });
  }
}
